import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { IconChevronDown, IconChevronUp, IconFilter, IconX } from "@tabler/icons-react";
import {
  asyncAutocompleteListboxProps,
  buildCustomerSearchUrl,
  noLocalFilter,
  sameEntityId,
  useAsyncAutocompleteInputState,
  useAsyncEntitySearch,
} from "utils/entity-autocomplete";

const FilterSection = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{ mb: 1.5, fontWeight: 600 }}
    >
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DateRangeFilter = ({
  fromLabel,
  toLabel,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  gridSize = 6,
}) => (
  <Grid item xs={12} md={gridSize}>
    <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
      <TextField
        label={fromLabel}
        type="date"
        value={fromValue || ""}
        onChange={(e) => onFromChange(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 0 }}
      />
      <TextField
        label={toLabel}
        type="date"
        value={toValue || ""}
        onChange={(e) => onToChange(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 0 }}
      />
    </Box>
  </Grid>
);

const EMPTY_FILTERS = {
  customer: null,
  customer_cpf: "",
  attendant_id: "",
  created_at_start: "",
  created_at_end: "",
  subject: "",
  status: "",
  process_number: "",
};

const AttendancesFilters = ({
  filters,
  onFiltersChange,
  onClearFilters,
  subjects = [],
  statuses = [],
  users = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const {
    inputValue: customerInputValue,
    setInputValue: setCustomerInput,
    bindInputChange: bindCustomerInputChange,
    clearInput: clearCustomerInput,
  } = useAsyncAutocompleteInputState();

  useEffect(() => {
    setLocalFilters(filters);
    if (filters.customer?.name) {
      setCustomerInput(filters.customer.name);
    } else {
      clearCustomerInput();
    }
  }, [filters, setCustomerInput, clearCustomerInput]);

  const selectedCustomer = localFilters.customer || null;
  const {
    options: customerOptions,
    loading: customersLoading,
    search: searchCustomers,
    listboxKey: customerListboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildCustomerSearchUrl,
    selectedEntity: selectedCustomer,
  });

  const handleFilterChange = (field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value || "" }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleClearFilters = () => {
    setLocalFilters({ ...EMPTY_FILTERS });
    clearCustomerInput();
    onClearFilters();
  };

  const getUserLabel = (option) => {
    if (!option) return "";
    if (option.first_name || option.last_name) {
      return `${option.first_name || ""} ${option.last_name || ""}`.trim() || option.username;
    }
    return option.username || "";
  };

  const activeFiltersCount = Object.values(localFilters).filter((value) => {
    if (value === null || value === undefined || value === "") return false;
    if (typeof value === "object") return Boolean(value.id);
    return true;
  }).length;

  const selectedAttendant =
    users.find((user) => Number(user.id) === Number(localFilters.attendant_id)) || null;
  const selectedSubject =
    subjects.find((subject) => subject.value === localFilters.subject) || null;
  const selectedStatus =
    statuses.find((item) => item.value === localFilters.status) || null;

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: expanded ? "background.paper" : "transparent",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconFilter stroke={1.5} size="1.2rem" />
            <Typography variant="h6">Filtros Avançados</Typography>
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton size="small">
            {expanded ? (
              <IconChevronUp stroke={1.5} size="1.2rem" />
            ) : (
              <IconChevronDown stroke={1.5} size="1.2rem" />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 3 }}>
            <FilterSection title="Cliente">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={customerOptions}
                  getOptionLabel={(option) => option?.name || ""}
                  value={selectedCustomer}
                  inputValue={customerInputValue}
                  onChange={(event, newValue) => {
                    handleFilterChange("customer", newValue || null);
                    if (newValue?.name) {
                      setCustomerInput(newValue.name);
                    } else {
                      clearCustomerInput();
                    }
                  }}
                  onInputChange={bindCustomerInputChange(searchCustomers)}
                  onOpen={() => searchCustomers(customerInputValue.trim())}
                  loading={customersLoading}
                  filterOptions={noLocalFilter}
                  ListboxProps={asyncAutocompleteListboxProps(customerListboxKey)}
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Selecione o cliente..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="CPF do cliente"
                  value={localFilters.customer_cpf || ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "").slice(0, 11);
                    const formatted = raw
                      .replace(/(\d{3})(\d)/, "$1.$2")
                      .replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
                      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
                    handleFilterChange("customer_cpf", formatted);
                  }}
                  fullWidth
                  size="small"
                  placeholder="000.000.000-00"
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Número do processo"
                  value={localFilters.process_number || ""}
                  onChange={(e) => handleFilterChange("process_number", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </FilterSection>

            <Divider sx={{ mb: 2.5 }} />

            <FilterSection title="Atendimento">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={users}
                  getOptionLabel={getUserLabel}
                  value={selectedAttendant}
                  onChange={(event, newValue) =>
                    handleFilterChange("attendant_id", newValue?.id || "")
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Atendente"
                      placeholder="Selecione o atendente..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={subjects}
                  getOptionLabel={(option) => option.label || ""}
                  value={selectedSubject}
                  onChange={(event, newValue) =>
                    handleFilterChange("subject", newValue?.value || "")
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assunto"
                      placeholder="Selecione o assunto..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={statuses}
                  getOptionLabel={(option) => option.label || ""}
                  value={selectedStatus}
                  onChange={(event, newValue) =>
                    handleFilterChange("status", newValue?.value || "")
                  }
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      placeholder="Selecione o status..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
            </FilterSection>

            <Divider sx={{ mb: 2.5 }} />

            <FilterSection title="Datas">
              <DateRangeFilter
                fromLabel="Data do atendimento (Início)"
                toLabel="Data do atendimento (Fim)"
                fromValue={localFilters.created_at_start}
                toValue={localFilters.created_at_end}
                onFromChange={(value) => handleFilterChange("created_at_start", value)}
                onToChange={(value) => handleFilterChange("created_at_end", value)}
              />
            </FilterSection>

            <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<IconX stroke={1.5} size="1rem" />}
              >
                Limpar Filtros
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                startIcon={<IconFilter stroke={1.5} size="1rem" />}
              >
                Aplicar Filtros
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default AttendancesFilters;
